/* Shared FAQ styles (`<FaqPanel>` + `<FaqBar>`).
 *
 * Everything is scoped under `.nw-faq` / `.nw-faq-bar` and the design tokens
 * are defined there too, so the components render identically in the dashboard
 * SPA AND inside the embeddable widget's shadow DOM (which only has its own
 * `--nw-*` tokens). Hosts load this file: the dashboard imports
 * `@netwatchai/chat-core/faq.css`; the widget inlines it into its shadow root.
 * Ported from the dashboard's app.css block, which this replaces.
 *
 * LIGHT / DARK — same convention as outlook.css: a DARK palette is the default
 * and a `.nw-theme-light` ancestor flips the lightness-dependent tokens with no
 * JS. Keep every surface colour in a token — never a hard-coded hex.
 *
 * WIDTH is the host's: the dashboard centres both blocks in `--chat-col`, the
 * widget lets them fill its panel. Neither dimension lives here. */

.nw-faq,
.nw-faq-bar {
  /* design tokens — DARK palette (default). Light overrides are at EOF. */
  --card: #141b25;
  --line: rgba(255, 255, 255, 0.07);
  --line2: rgba(255, 255, 255, 0.12);
  --ink: #e7ecf3;
  --soft: #98a4b4;
  --faint: #5e6b7c;
  --accent: #5b93e8;
  --accent-ink: #93bdf5;
  --accent-soft: rgba(91, 147, 232, 0.1);
  --amber: #d99a3a;
  --chip-bg: rgba(255, 255, 255, 0.03);
  --hover: rgba(255, 255, 255, 0.04);
  --hover-3: rgba(255, 255, 255, 0.06);
  --scroll-thumb: rgba(255, 255, 255, 0.14);
  --font-display: 'Saira', system-ui, sans-serif;
  --font-body: 'Barlow', system-ui, sans-serif;
  font-family: var(--font-body);
  color: var(--ink);
}
.nw-faq *, .nw-faq-bar * { box-sizing: border-box; }

/* --- the slide-up panel ---------------------------------------------------- */

.nw-faq {
  display: none;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 13px;
  padding: 8px 10px 12px;
  max-height: 300px;
  overflow-y: auto;
}
.nw-faq.nw-faq-open { display: block; }
.nw-faq::-webkit-scrollbar { width: 9px; }
.nw-faq::-webkit-scrollbar-thumb { background: var(--scroll-thumb); border-radius: 8px; }

/* Add form. It wraps by width, not by viewport: the widget's panel is narrow on
   a desktop too, and a 120px category box beside the question box leaves the
   question unreadable ("Add your own qu…"). The basis keeps both on one row
   wherever there's room (the dashboard's 820px column). */
.nw-faq-add { display: flex; flex-wrap: wrap; gap: 6px; margin: 4px 2px 8px; }
.nw-faq-add input {
  flex: 1 1 170px; min-width: 0;
  font-family: inherit; font-size: 12px; color: var(--ink);
  background: var(--hover); border: 1px solid var(--line); border-radius: 8px;
  padding: 6px 9px;
}
.nw-faq-add input::placeholder { color: var(--faint); }
.nw-faq-catwrap { position: relative; flex: 0 1 120px; display: flex; }
.nw-faq-add input.nw-faq-cat-in { flex: 1; min-width: 0; }
.nw-faq-catmenu {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 4;
  display: flex; flex-direction: column; gap: 1px;
  background: var(--card); border: 1px solid var(--line2); border-radius: 9px;
  padding: 4px; box-shadow: 0 10px 24px rgba(0, 0, 0, .45);
}
.nw-faq-catopt {
  text-align: left; font-family: inherit; font-size: 12px; color: var(--soft);
  background: none; border: none; border-radius: 6px; padding: 6px 8px;
  cursor: pointer; transition: .12s;
}
.nw-faq-catopt:hover { background: var(--accent-soft); color: var(--accent-ink); }
.nw-faq-add button {
  flex: none; font-family: inherit; font-size: 12px; font-weight: 600; color: var(--soft);
  border: 1px solid var(--line); background: var(--chip-bg); border-radius: 8px;
  padding: 6px 12px; cursor: pointer; transition: .15s;
}
.nw-faq-add button:hover:not(:disabled) { border-color: var(--accent); color: var(--accent-ink); }
.nw-faq-add button:disabled { opacity: .45; cursor: default; }

/* grouped rows */
.nw-faq-cat {
  font-family: var(--font-display); font-size: 10px; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase; color: var(--faint);
  margin: 12px 6px 5px;
}
.nw-faq-cat:first-child { margin-top: 4px; }
.nw-faq-row { display: flex; align-items: center; gap: 2px; }
.nw-faq-q {
  display: block; width: 100%; text-align: left;
  font-family: inherit; font-size: 13px; color: var(--ink);
  background: none; border: none; border-radius: 8px; padding: 8px 10px;
  cursor: pointer; transition: .12s;
}
.nw-faq-q:hover:not(:disabled) { background: var(--accent-soft); color: var(--accent-ink); }
.nw-faq-q:disabled { cursor: default; }
.nw-faq-row .nw-faq-q { flex: 1; min-width: 0; }
.nw-faq-star, .nw-faq-del {
  flex: none; display: inline-flex; align-items: center; color: var(--faint);
  background: none; border: none; border-radius: 6px; padding: 5px;
  cursor: pointer; transition: .12s;
}
.nw-faq-star:hover:not(:disabled), .nw-faq-del:hover:not(:disabled) { color: var(--ink); background: var(--hover-3); }
.nw-faq-star.on { color: var(--amber); }
.nw-faq-star.on svg { fill: currentColor; }
.nw-faq-star:disabled, .nw-faq-del:disabled { opacity: .35; cursor: default; }

/* states */
.nw-faq-err { font-size: 12px; color: var(--amber); margin: 2px 6px 6px; }
.nw-faq-retry {
  font-size: inherit; font-family: inherit; letter-spacing: inherit;
  text-transform: inherit; color: var(--accent); background: none; border: none;
  padding: 0; cursor: pointer; text-decoration: underline;
}

/* --- the pinned bar -------------------------------------------------------- */

.nw-faq-bar { display: flex; align-items: center; gap: 9px; }
.nw-faq-barlabel {
  flex: none; font-size: 10px; font-weight: 700; letter-spacing: .08em;
  text-transform: uppercase; color: var(--faint);
  display: inline-flex; align-items: center; gap: 5px;
}
.nw-faq-barlabel svg { color: var(--amber); }
.nw-faq-chips { display: flex; gap: 8px; flex-wrap: wrap; flex: 1; min-width: 0; }
.nw-faq-chip {
  font-family: inherit; font-size: 12px; color: var(--soft);
  border: 1px solid var(--line); background: var(--chip-bg); border-radius: 999px;
  padding: 6px 12px; cursor: pointer; transition: .15s;
}
.nw-faq-chip:hover:not(:disabled) { border-color: var(--line2); color: var(--ink); }
.nw-faq-chip:disabled { cursor: default; }
.nw-faq-btn {
  flex: none; font-family: inherit; font-size: 12px; font-weight: 600; color: var(--soft);
  border: 1px solid var(--line); background: var(--chip-bg); border-radius: 999px;
  padding: 6px 12px; cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px; transition: .15s;
}
.nw-faq-btn:hover { border-color: var(--accent); color: var(--accent-ink); }

/* --- PHONES ---------------------------------------------------------------- */
/* Same 560px breakpoint the chat column uses: on a phone the panel and the
   transcript are fighting over one screen, so give the transcript more of it. */
@media (max-width: 560px) {
  .nw-faq { max-height: 240px; padding: 6px 8px 10px; }
}

/* --- LIGHT theme ----------------------------------------------------------- */
.nw-theme-light .nw-faq,
.nw-theme-light .nw-faq-bar {
  --card: #ffffff;
  --line: rgba(17, 24, 39, 0.10);
  --line2: rgba(17, 24, 39, 0.17);
  --ink: #111827;
  --soft: #566069;
  --faint: #828b99;
  --accent: #2f6fd0; /* deeper blue for AA contrast on white */
  --accent-ink: #2f6fd0;
  --accent-soft: rgba(47, 111, 208, 0.08);
  --chip-bg: rgba(17, 24, 39, 0.02);
  --hover: rgba(17, 24, 39, 0.03);
  --hover-3: rgba(17, 24, 39, 0.05);
  --scroll-thumb: rgba(17, 24, 39, 0.18);
}
.nw-theme-light .nw-faq-catmenu { box-shadow: 0 10px 24px rgba(17, 24, 39, .16); }
